/**
 * 连接错误检测工具
 * 用于检测浏览器扩展连接错误和浏览器兼容性问题
 */

export interface ConnectionError {
  type: 'browser_extension' | 'browser_compatibility' | 'network' | 'unknown'
  message: string
  timestamp: number
}

/**
 * 检测浏览器扩展连接错误
 * 这个错误通常出现在浏览器扩展与页面通信失败时
 */
export const detectBrowserExtensionError = (): boolean => {
  if (typeof window === 'undefined') return false
  
  // 监听控制台错误
  const originalConsoleError = console.error
  console.error = function(...args: any[]) {
    const errorMessage = args.join(' ')
    
    // 检测特定的连接错误
    if (errorMessage.includes('Could not establish connection') && 
        errorMessage.includes('Receiving end does not exist')) {
      console.warn('检测到浏览器扩展连接错误:', errorMessage)
      
      // 保存错误信息到sessionStorage
      const error: ConnectionError = {
        type: 'browser_extension',
        message: errorMessage,
        timestamp: Date.now()
      }
      
      sessionStorage.setItem('connectionError', JSON.stringify(error))
      
      // 触发浏览器升级页面跳转
      if (typeof window !== 'undefined' && window.location) {
        const currentPath = window.location.pathname
        if (!currentPath.includes('/browserUpgrade')) {
          // 获取当前语言
          const lang = currentPath.split('/')[1] || 'en'
          window.location.href = `/${lang}/browserUpgrade`
        }
      }
      
      return true
    }
    
    // 调用原始console.error
    return originalConsoleError.apply(console, args)
  }
  
  return false
}

/**
 * 检测浏览器兼容性错误
 */
export const detectBrowserCompatibilityError = (): boolean => {
  if (typeof window === 'undefined') return false
  
  try {
    // 检测现代JavaScript特性支持
    const features = {
      // 检测逻辑赋值运算符支持
      logicalAssignment: (() => {
        try {
          let a = false
          a ||= true
          return true
        } catch {
          return false
        }
      })(),
      
      // 检测可选链操作符支持
      optionalChaining: (() => {
        try {
          const obj: any = {}
          const result = obj?.property?.nested
          return true
        } catch {
          return false
        }
      })(),
      
      // 检测空值合并操作符支持
      nullishCoalescing: (() => {
        try {
          const value: any = null
          const result = value ?? 'default'
          return result === 'default'
        } catch {
          return false
        }
      })(),
      
      // 检测Promise.allSettled支持
      promiseAllSettled: (() => {
        try {
          return typeof Promise.allSettled === 'function'
        } catch {
          return false
        }
      })(),
      
      // 检测BigInt支持
      bigInt: (() => {
        try {
          return typeof BigInt === 'function'
        } catch {
          return false
        }
      })()
    }
    
    // 如果关键特性不支持，则认为浏览器不兼容
    const criticalFeatures = ['logicalAssignment', 'optionalChaining', 'nullishCoalescing']
    const isCompatible = criticalFeatures.every(feature => features[feature as keyof typeof features])
    
    if (!isCompatible) {
      const error: ConnectionError = {
        type: 'browser_compatibility',
        message: 'Browser does not support required JavaScript features',
        timestamp: Date.now()
      }
      
      sessionStorage.setItem('connectionError', JSON.stringify(error))
      
      // 跳转到浏览器升级页面
      const currentPath = window.location.pathname
      if (!currentPath.includes('/browserUpgrade')) {
        const lang = currentPath.split('/')[1] || 'en'
        window.location.href = `/${lang}/browserUpgrade`
      }
      
      return true
    }
    
    return false
  } catch (error) {
    console.error('检测浏览器兼容性时出错:', error)
    return false
  }
}

/**
 * 检测网络连接错误
 */
export const detectNetworkError = (): boolean => {
  if (typeof window === 'undefined') return false
  
  // 监听网络状态变化
  if ('navigator' in window && 'onLine' in navigator) {
    const handleOnlineStatusChange = () => {
      if (!navigator.onLine) {
        const error: ConnectionError = {
          type: 'network',
          message: 'Network connection lost',
          timestamp: Date.now()
        }
        
        sessionStorage.setItem('connectionError', JSON.stringify(error))
      }
    }
    
    window.addEventListener('online', handleOnlineStatusChange)
    window.addEventListener('offline', handleOnlineStatusChange)
  }
  
  return false
}

/**
 * 初始化连接错误检测
 */
export const initConnectionErrorDetection = (): void => {
  if (typeof window === 'undefined') return
  
//   const { $vconsole } = useNuxtApp()
//   if ($vconsole) {
//     $vconsole.show()
//   }
  // 检测浏览器扩展错误
  let isBrowserExtensionError = detectBrowserExtensionError()
  
  // 检测浏览器兼容性错误
  let isBrowserCompatibilityError = detectBrowserCompatibilityError()
  
  // 检测网络错误
  let isNetworkError = detectNetworkError()

  // 监听未捕获的错误
  window.addEventListener('error', (event) => {
    const errorMessage = event.message || event.error?.message || ''
    
    if (errorMessage.includes('Could not establish connection') || 
        errorMessage.includes('Receiving end does not exist') ||
        errorMessage.includes('Extension context invalidated')) {
      
      const error: ConnectionError = {
        type: 'browser_extension',
        message: errorMessage,
        timestamp: Date.now()
      }
      
      sessionStorage.setItem('connectionError', JSON.stringify(error))
      
      // 跳转到浏览器升级页面
      const currentPath = window.location.pathname
      if (!currentPath.includes('/browserUpgrade')) {
        const lang = currentPath.split('/')[1] || 'en'
        window.location.href = `/${lang}/browserUpgrade`
      }
    }
  })
  
  // 监听未捕获的Promise拒绝
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || String(event.reason) || ''
    
    if (errorMessage.includes('Could not establish connection') || 
        errorMessage.includes('Receiving end does not exist') ||
        errorMessage.includes('Extension context invalidated')) {
      
      const error: ConnectionError = {
        type: 'browser_extension',
        message: errorMessage,
        timestamp: Date.now()
      }
      
      sessionStorage.setItem('connectionError', JSON.stringify(error))
      // 跳转到浏览器升级页面
      const currentPath = window.location.pathname
      if (!currentPath.includes('/browserUpgrade')) {
        const lang = currentPath.split('/')[1] || 'en'
        window.location.href = `/${lang}/browserUpgrade`
      }
    }
  })
}

/**
 * 获取存储的连接错误
 */
export const getStoredConnectionError = (): ConnectionError | null => {
  if (typeof window === 'undefined') return null
  
  try {
    const errorStr = sessionStorage.getItem('connectionError')
    if (errorStr) {
      return JSON.parse(errorStr)
    }
  } catch (error) {
    console.error('解析存储的连接错误时出错:', error)
  }
  
  return null
}

/**
 * 清除存储的连接错误
 */
export const clearStoredConnectionError = (): void => {
  if (typeof window === 'undefined') return
  
  sessionStorage.removeItem('connectionError')
}
